import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { postRequest } from '../../../lib/api'; // adjust path as needed

const VAT_RATE = 0.2;

const LssLastingPowerOfAttorney = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { product_id, partner_id } = location.state || {};
  const { title } = useParams();
  const [editId, seteditId] = useState(null);

  const slugToTitle = (slug) => {
    return slug
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const readableTitle = slugToTitle(title || '');

  const initialRows = [
    { label: 'Health & Welfare or Property & Finance', docs: '1 doc in total', documentsCount: 1 },
    { label: 'Health & Welfare or Property & Finance', docs: '2 docs in total', documentsCount: 2 },
  ];

  const coupleRows = [
    { label: 'Health & Welfare or Property & Finance', docs: '2 docs in total', documentsCount: 2 },
    { label: 'Health & Welfare or Property & Finance', docs: '4 docs in total', documentsCount: 4 },
  ];

  const [individualCosts, setIndividualCosts] = useState([
    { mlmLegalCost: '', vat: 0, totalCost: 0 },
    { mlmLegalCost: '', vat: 0, totalCost: 0 },
  ]);

  const [coupleCosts, setCoupleCosts] = useState([
    { mlmLegalCost: '', vat: 0, totalCost: 0 },
    { mlmLegalCost: '', vat: 0, totalCost: 0 },
  ]);

  const handleChange = (index, value, type) => {
    const cost = parseFloat(value) || 0;
    const vat = parseFloat((cost * VAT_RATE).toFixed(2));
    const total = parseFloat((cost + vat).toFixed(2));

    const updated = [...(type === 'individual' ? individualCosts : coupleCosts)];
    updated[index] = { mlmLegalCost: value, vat, totalCost: total };

    type === 'individual'
      ? setIndividualCosts(updated)
      : setCoupleCosts(updated);
  };

 const handleSubmit = async () => {
  const individual = initialRows.map((row, idx) => ({
    typeOfLpa: row.label,
    documentsCount: row.documentsCount,
    mlmLegalCost: parseFloat(individualCosts[idx].mlmLegalCost || '0'),
    vat: individualCosts[idx].vat,
    totalCost: individualCosts[idx].totalCost,
  }));

  const couple = coupleRows.map((row, idx) => ({
    typeOfLpa: row.label,
    documentsCount: row.documentsCount,
    mlmLegalCost: parseFloat(coupleCosts[idx].mlmLegalCost || '0'),
    vat: coupleCosts[idx].vat,
    totalCost: coupleCosts[idx].totalCost,
  }));

  try {
    const payload = {
      product_id,
      partner_id,
      individual,
      couple,
    };

    // Use partner-specific endpoint
    const res = await postRequest('private/createPartnerLpaRateCard', payload);

    Swal.fire({
      icon: 'success',
      title: 'Success',
      text: res?.message || (editId ? 'LPA cost updated.' : 'LPA cost created.'),
    }).then(() => navigate(-1));
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text:
        err?.response?.data?.message ||
        'Something went wrong while submitting LPA cost.',
    });
  }
};


  


  React.useEffect(() => {
  const fetchExistingLpaCosts = async () => {
    try {
     // Use partner-specific endpoint
     const res = await postRequest(
       'private/getPartnerLpaRateCard',
       { partner_id, product_id }
     );

     seteditId(res?.data?._id)

      const { individual = [], couple = [] } = res?.data || {};

      const formatCosts = (costArray) =>
        costArray.map((item) => ({
          mlmLegalCost: item?.mlmLegalCost?.toString() || '',
          vat: item?.vat || 0,
          totalCost: item?.totalCost || 0,
        }));

      setIndividualCosts(formatCosts(individual));
      setCoupleCosts(formatCosts(couple));
    } catch (error) {
      console.error('Failed to fetch partner LPA rate card', error);
    }
  };

  if (partner_id && product_id) fetchExistingLpaCosts();
}, [partner_id, product_id]);


  const renderTable = (rows, costs, type) => (
    <TableContainer component={Paper} elevation={2} sx={{ mb: 4 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f4f4f4' }}>
            <TableCell><strong>Type of LPA</strong></TableCell>
            <TableCell><strong>Documents</strong></TableCell>
            <TableCell><strong>MLM Legal Cost</strong></TableCell>
            <TableCell><strong>VAT</strong></TableCell>
            <TableCell><strong>Total Cost</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell>{row.label}</TableCell>
              <TableCell>{row.docs}</TableCell>
              <TableCell>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="£"
                  type="number"
                  value={costs[idx].mlmLegalCost}
                  onChange={(e) => handleChange(idx, e.target.value, type)}
                />
              </TableCell>
              <TableCell>£{costs[idx].vat.toFixed(2)}</TableCell>
              <TableCell>£{costs[idx].totalCost.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box p={4}>
      <Typography variant="h5" fontWeight="600" mb={2}>
        Manage {readableTitle}
      </Typography>

      <Typography variant="h6" fontWeight="500" mt={3} mb={1}>
        For Individual
      </Typography>
      {renderTable(initialRows, individualCosts, 'individual')}

      <Typography variant="h6" fontWeight="500" mt={3} mb={1}>
        For a Couple
      </Typography>
      {renderTable(coupleRows, coupleCosts, 'couple')}

      <Box mt={2} display="flex" gap={2}>
        <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          className="red-bg red-button text-capitalize"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default LssLastingPowerOfAttorney;
